import React, { useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../components/AuthContext';
import toast from 'react-hot-toast';
import PhoneInput from '../components/PhoneInput';
import { UserPlus, Mail, Lock, User, Stethoscope, Eye, EyeOff, Shield, Zap, MessageCircle, FileText, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { label: 'Very weak', color: 'bg-red-500' },
    { label: 'Weak',      color: 'bg-orange-500' },
    { label: 'Fair',      color: 'bg-amber-500' },
    { label: 'Good',      color: 'bg-lime-500' },
    { label: 'Strong',    color: 'bg-emerald-500' },
  ];
  return { score, ...levels[Math.min(score, 4)] };
};

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [registered, setRegistered] = useState(null); // { role }
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { register: authRegister, loading, clearError } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const passwordValue = watch('password', '');
  const roleValue = watch('role', '');
  const strength = useMemo(() => getPasswordStrength(passwordValue), [passwordValue]);

  const [phoneValue, setPhoneValue] = useState('');

  const onSubmit = async (data) => {
    clearError();
    const result = await authRegister(data.name, data.email, data.password, data.role, data.licenseNumber, phoneValue, data.specialization, turnstileToken);
    if (result.success) {
      if (data.role === 'DOCTOR') {
        setRegistered({ role: 'DOCTOR' });
      } else {
        toast.success('Account created! Welcome to SymptoConnect.');
        navigate('/onboarding');
      }
    } else {
      toast.error(result.error || 'Registration failed');
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  const inputClass = (hasError) =>
    `block w-full pl-10 pr-4 py-3 bg-surface-container-highest border ${
      hasError ? 'border-danger-400 ring-1 ring-danger-400' : 'border-outline-variant/20'
    } text-surface-900 placeholder-surface-400 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`;

  return (
    <div className="min-h-screen flex">
      {/* Left — branding panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden items-center justify-center p-12 bg-gradient-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(0,71,141,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(0,105,107,0.1),transparent_50%),radial-gradient(ellipse_at_center,rgba(0,94,184,0.08),transparent_60%)]"></div>
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-400/10 rounded-full blur-[80px] animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/8 rounded-full blur-[100px] animate-float" style={{ animationDelay: '2s' }}></div>
        
        {/* Grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary-400 to-primary-500 shadow-elevated">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">Sympto<span className="text-primary-300">Connect</span></span>
          </div>
          
          <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight tracking-tight">
            Join thousands of patients<br />and doctors today
          </h2>
          <p className="mt-4 text-primary-200/80 leading-relaxed">
            Create your free account and start connecting with healthcare professionals in minutes.
          </p>
          
          {/* Feature badges */}
          <div className="mt-10 grid grid-cols-2 gap-3">
            {[
              { Icon: Shield, label: 'End-to-end security' },
              { Icon: Zap, label: 'Real-time updates' },
              { Icon: Stethoscope, label: 'Expert doctors' },
              { Icon: MessageCircle, label: 'Private messaging' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-xl px-3.5 py-2.5 hover:bg-white/[0.07] transition-colors">
                <item.Icon className="w-4 h-4 text-primary-300" />
                <span className="text-xs text-primary-100/80 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
          
          {/* Abstract preview */}
          <div className="mt-10 bg-white/[0.05] backdrop-blur-sm rounded-2xl p-5 border border-white/[0.06]">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-danger-400/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-warning-400/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-success-400/80"></div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-400/15 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-primary-300" />
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-white/10 rounded-full w-3/4 mb-2"></div>
                  <div className="h-2 bg-white/5 rounded-full w-1/2"></div>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="h-8 bg-white/5 rounded-lg flex-1"></div>
                <div className="h-8 bg-primary-400/15 rounded-lg flex-1"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center bg-surface-50 px-4 py-12 sm:px-8">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <Link to="/" className="flex lg:hidden items-center justify-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-600 shadow-elevated">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-surface-900 tracking-tight">
              Sympto<span className="text-primary-500">Connect</span>
            </span>
          </Link>

          <div className="mb-6 text-center">
            <Link to="/welcome" className="text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors">
              Back to Welcome
            </Link>
          </div>

          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 tracking-tight">
              Create Account
            </h1>
            <p className="mt-2 text-sm text-surface-500">
              Fill in the details below to get started
            </p>
          </div>

          {registered?.role === 'DOCTOR' ? (
            <div className="mt-10 text-center space-y-5">
              <div className="w-16 h-16 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 mb-2">Account Created — Pending Verification</h2>
                <p className="text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">
                  Your doctor account is under review. An admin will verify your medical license before you can access patient data. You'll receive an email once approved.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-left space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-700">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Account created successfully
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-700">
                  <Info className="w-4 h-4 flex-shrink-0" /> License verification in progress
                </div>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 bg-slate-900 text-white rounded-full font-bold text-sm hover:bg-primary transition-all"
              >
                Go to Sign In
              </button>
            </div>
          ) : (
          <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)}>
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-surface-700 mb-1.5">Full Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><User className="h-[18px] w-[18px] text-surface-400" /></div>
                <input id="name" type="text" autoComplete="name" className={inputClass(errors.name)} placeholder="Enter your full name"
                  {...register('name', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })} />
              </div>
              {errors.name && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.name.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-surface-700 mb-1.5">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><Mail className="h-[18px] w-[18px] text-surface-400" /></div>
                <input id="email" type="email" autoComplete="email" className={inputClass(errors.email)} placeholder="you@example.com"
                  {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' } })} />
              </div>
              {errors.email && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.email.message}</p>}
            </div>

            {/* Role selector */}
            <div>
              <label className="block text-sm font-semibold text-surface-700 mb-1.5">I am a</label>
              <div className="grid grid-cols-2 gap-3">
                <label className="cursor-pointer">
                  <input type="radio" value="USER" className="sr-only" {...register('role', { required: 'Please select an account type' })} />
                  <div className={`flex flex-col items-center gap-2 px-4 py-4 border-2 rounded-xl transition-all ${watch('role') === 'USER' ? 'border-primary bg-primary-50 shadow-elevated' : 'border-outline-variant/20 bg-white hover:border-surface-300'}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${watch('role') === 'USER' ? 'bg-primary text-white' : 'bg-surface-100 text-surface-500'}`}>
                      <User size={20} />
                    </div>
                    <span className={`text-sm font-semibold ${watch('role') === 'USER' ? 'text-primary-700' : 'text-surface-600'}`}>Patient</span>
                  </div>
                </label>
                <label className="cursor-pointer">
                  <input type="radio" value="DOCTOR" className="sr-only" {...register('role', { required: 'Please select an account type' })} />
                  <div className={`flex flex-col items-center gap-2 px-4 py-4 border-2 rounded-xl transition-all ${watch('role') === 'DOCTOR' ? 'border-secondary bg-secondary-50 shadow-elevated' : 'border-outline-variant/20 bg-white hover:border-surface-300'}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${watch('role') === 'DOCTOR' ? 'bg-secondary-600 text-white' : 'bg-surface-100 text-surface-500'}`}>
                      <Stethoscope size={20} />
                    </div>
                    <span className={`text-sm font-semibold ${watch('role') === 'DOCTOR' ? 'text-secondary-700' : 'text-surface-600'}`}>Doctor</span>
                  </div>
                </label>
              </div>
              {errors.role && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.role.message}</p>}
            </div>

            {/* Phone number */}
            <div>
              <label className="block text-sm font-semibold text-surface-700 mb-1.5">Phone Number <span className="font-normal text-surface-400">(optional)</span></label>
              <PhoneInput value={phoneValue} onChange={setPhoneValue} />
            </div>

            {/* License Number - Only for doctors */}
            {roleValue === 'DOCTOR' && (
              <div>
                <label htmlFor="licenseNumber" className="block text-sm font-semibold text-surface-700 mb-1.5">Medical License Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <svg className="h-[18px] w-[18px] text-surface-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <input 
                    id="licenseNumber" 
                    type="text" 
                    autoComplete="off" 
                    className={inputClass(errors.licenseNumber)} 
                    placeholder="Enter your medical license number"
                    {...register('licenseNumber', { 
                      required: roleValue === 'DOCTOR' ? 'Medical license number is required for doctors' : false,
                      minLength: { value: 3, message: 'License number must be at least 3 characters' }
                    })} 
                  />
                </div>
                {errors.licenseNumber && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.licenseNumber.message}</p>}
              </div>
            )}

            {/* Specialization - Only for doctors */}
            {roleValue === 'DOCTOR' && (
              <div>
                <label htmlFor="specialization" className="block text-sm font-semibold text-surface-700 mb-1.5">Specialization <span className="font-normal text-surface-400">(optional)</span></label>
                <select id="specialization" className={inputClass(errors.specialization)}
                  {...register('specialization')}>
                  <option value="">Select specialization</option>
                  {[
                    'General Practice','Family Medicine','Internal Medicine',
                    'Cardiology','Dermatology','Emergency Medicine',
                    'Endocrinology','Gastroenterology','Geriatrics',
                    'Hematology','Infectious Disease','Nephrology',
                    'Neurology','Obstetrics & Gynecology','Oncology',
                    'Ophthalmology','Orthopedics','Otolaryngology (ENT)',
                    'Pediatrics','Psychiatry','Pulmonology',
                    'Radiology','Rheumatology','Surgery (General)',
                    'Urology','Other',
                  ].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.specialization && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.specialization.message}</p>}
              </div>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-surface-700 mb-1.5">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><Lock className="h-[18px] w-[18px] text-surface-400" /></div>
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                  className={`block w-full pl-10 pr-11 py-3 bg-surface-container-highest border ${errors.password ? 'border-danger-400 ring-1 ring-danger-400' : 'border-outline-variant/20'} text-surface-900 placeholder-surface-400 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                  placeholder="Min. 6 characters"
                  {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} />
                <button type="button" className="absolute inset-y-0 right-0 pr-3.5 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-[18px] w-[18px] text-surface-400 hover:text-surface-600 transition-colors" /> : <Eye className="h-[18px] w-[18px] text-surface-400 hover:text-surface-600 transition-colors" />}
                </button>
              </div>
              {/* Strength bar */}
              {passwordValue && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[0,1,2,3,4].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i < strength.score ? strength.color : 'bg-slate-200'}`} />
                    ))}
                  </div>
                  <p className={`text-xs font-semibold ${
                    strength.score <= 1 ? 'text-red-500' :
                    strength.score <= 2 ? 'text-amber-500' :
                    strength.score <= 3 ? 'text-lime-600' : 'text-emerald-600'
                  }`}>{strength.label}</p>
                </div>
              )}
              {errors.password && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.password.message}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-surface-700 mb-1.5">Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><Lock className="h-[18px] w-[18px] text-surface-400" /></div>
                <input id="confirmPassword" type={showConfirm ? 'text' : 'password'} autoComplete="new-password"
                  className={`block w-full pl-10 pr-11 py-3 bg-surface-container-highest border ${errors.confirmPassword ? 'border-danger-400 ring-1 ring-danger-400' : 'border-outline-variant/20'} text-surface-900 placeholder-surface-400 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                  placeholder="Re-enter your password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: v => v === passwordValue || 'Passwords do not match'
                  })} />
                <button type="button" className="absolute inset-y-0 right-0 pr-3.5 flex items-center" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff className="h-[18px] w-[18px] text-surface-400 hover:text-surface-600 transition-colors" /> : <Eye className="h-[18px] w-[18px] text-surface-400 hover:text-surface-600 transition-colors" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.confirmPassword.message}</p>}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input id="terms" type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30 cursor-pointer flex-shrink-0"
                {...register('terms', { required: 'You must accept the terms to continue' })} />
              <label htmlFor="terms" className="text-xs text-surface-500 cursor-pointer leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" className="font-semibold text-primary-600 hover:underline">Terms of Service</Link>{' '}and{' '}
                <Link to="/privacy" className="font-semibold text-primary-600 hover:underline">Privacy Policy</Link>.
                {roleValue === 'DOCTOR' && ' I confirm my license details are accurate.'}
              </label>
            </div>
            {errors.terms && <p className="text-xs font-medium text-danger-600">{errors.terms.message}</p>}

            {/* Turnstile invisible bot protection */}
            <Turnstile
              ref={turnstileRef}
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              options={{ size: 'invisible' }}
            />

            {/* Submit */}
            <button type="submit" disabled={loading || !turnstileToken}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r from-primary to-primary-container text-white text-sm font-semibold rounded-full shadow-elevated hover:shadow-elevated-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all min-h-[48px]">
              {loading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div> Creating account...</>
              ) : (
                <><UserPlus className="h-4 w-4" /> Create Account</>
              )}
            </button>

            <p className="text-center text-sm text-surface-500 pt-2">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-500 transition-colors">Sign in</Link>
            </p>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;